import { Link, useParams } from 'react-router-dom'
import { OrderDetailSkeleton } from '../components/Skeleton'
import {
  ORDER_PATHS,
  ORDER_PROGRESS,
  ORDER_STATUS,
  ORDER_STATUS_LABEL,
  ORDER_UI,
} from '../constants/orders'
import { cx, formatCurrency, formatDateTime } from '../helpers/format'
import {
  deliveryPromiseLabel,
  formatDeliveryLine,
  orderItemAddonLabel,
  orderProgressIndex,
  orderStatusLabel,
  paymentStatusLabel,
} from '../helpers/orders'
import { useCanCancelOrder } from '../hooks/useCanCancelOrder'
import { useOrderDetail } from '../hooks/useOrderDetail'
import '../styles/orders-page.scss'

export function OrderDetailPage() {
  const { id } = useParams()
  const { order, siblings, loading, error, cancelling, paying, cancel, pay, justPlaced, placedSplit, needsPayment } =
    useOrderDetail(id)
  const canCancel = useCanCancelOrder(order)
  const progressIndex = order ? orderProgressIndex(order.status) : -1
  const isCancelled = order?.status === ORDER_STATUS.cancelled

  return (
    <div className="orders-page">
      <p className="orders-page__back">
        <Link to={ORDER_PATHS.list}>{ORDER_UI.backToOrders}</Link>
      </p>

      {justPlaced ? (
        <div className="orders-page__placed" role="status">
          <p>{ORDER_UI.placedTitle}</p>
          <p className="orders-page__placed-hint">
            {needsPayment
              ? ORDER_UI.placedPayHint
              : placedSplit
                ? ORDER_UI.placedSplitHint
                : ORDER_UI.placedHint}
          </p>
        </div>
      ) : null}

      {error ? <p className="orders-page__error">{error}</p> : null}

      {loading && !order ? (
        <OrderDetailSkeleton />
      ) : !order ? (
        <div className="orders-page__empty">
          <p>{ORDER_UI.notFound}</p>
          <Link to={ORDER_PATHS.list} className="orders-page__continue">
            {ORDER_UI.backToOrders}
          </Link>
        </div>
      ) : (
        <article className="order-detail">
          <header className="order-detail__head">
            <div>
              <h1>{order.orderNumber}</h1>
              <p className="order-card__meta">
                {ORDER_UI.placedOn} {formatDateTime(order.createdAt)}
              </p>
              {isCancelled && order.cancelReason ? (
                <p className="order-card__meta">{order.cancelReason}</p>
              ) : null}
            </div>
            <div className="order-card__badges">
              <span className={`order-status order-status--${order.status}`}>
                {orderStatusLabel(order.status)}
              </span>
              <span className={`order-sla${order.isExpress ? ' order-sla--express' : ''}`}>
                {deliveryPromiseLabel(order)}
              </span>
            </div>
          </header>

          {siblings.length > 0 ? (
            <section className="order-detail__card">
              <h2>{ORDER_UI.siblingHeading}</h2>
              <ul className="order-detail__siblings">
                {siblings.map((sibling) => (
                  <li key={sibling.id}>
                    <Link to={ORDER_PATHS.detail(sibling.id)}>
                      {sibling.orderNumber}
                    </Link>
                    <span>
                      {deliveryPromiseLabel(sibling)} ·{' '}
                      {formatCurrency(sibling.total)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {isCancelled ? null : (
            <ol className="order-progress">
              {ORDER_PROGRESS.map((status, index) => (
                <li
                  key={status}
                  className={cx(
                    'order-progress__step',
                    index <= progressIndex && 'is-done',
                    index === progressIndex && 'is-current',
                  )}
                >
                  <span>{ORDER_STATUS_LABEL[status]}</span>
                </li>
              ))}
            </ol>
          )}

          <section className="order-detail__card">
            <h2>{ORDER_UI.itemsHeading}</h2>
            <ul className="order-detail__items">
              {order.items.map((item) => {
                const addonLabel = orderItemAddonLabel(item.addons.map((addon) => addon.name))
                return (
                  <li key={item.id}>
                    <Link to={`/product/${item.productSlug}`} className="order-detail__media">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt="" />
                      ) : (
                        <span className="order-detail__placeholder" aria-hidden />
                      )}
                    </Link>
                    <div>
                      <Link to={`/product/${item.productSlug}`}>{item.productName}</Link>
                      <p className="order-card__meta">
                        {item.variantLabel}
                        {item.wrapperLabel ? ` · ${item.wrapperLabel}` : ''}
                      </p>
                      {addonLabel ? <p className="order-card__meta">{addonLabel}</p> : null}
                      <p className="order-card__meta">× {item.quantity}</p>
                    </div>
                    <strong>{formatCurrency(item.lineTotal)}</strong>
                  </li>
                )
              })}
            </ul>
          </section>

          <div className="order-detail__grid">
            <section className="order-detail__card">
              <h2>{ORDER_UI.deliveryHeading}</h2>
              <p>
                <strong>{ORDER_UI.recipientLabel}:</strong> {order.delivery.recipientName}
              </p>
              <p>
                <strong>{ORDER_UI.phoneLabel}:</strong> {order.delivery.recipientPhone}
              </p>
              <p>
                <strong>{ORDER_UI.addressLabel}:</strong> {formatDeliveryLine(order.delivery)}
              </p>
              <p>
                <strong>{ORDER_UI.deliveryPromiseLabel}:</strong>{' '}
                {deliveryPromiseLabel(order)}
              </p>
              {order.delivery.notes ? (
                <p>
                  <strong>{ORDER_UI.notesLabel}:</strong> {order.delivery.notes}
                </p>
              ) : null}
            </section>

            {order.billing ? (
              <section className="order-detail__card">
                <h2>{ORDER_UI.billingHeading}</h2>
                {order.billing.sameAsShipping ? (
                  <p>{ORDER_UI.billingSameAsShipping}</p>
                ) : null}
                <p>
                  <strong>{ORDER_UI.recipientLabel}:</strong> {order.billing.name}
                </p>
                <p>
                  <strong>{ORDER_UI.phoneLabel}:</strong> {order.billing.phone}
                </p>
                <p>
                  <strong>{ORDER_UI.addressLabel}:</strong>{' '}
                  {formatDeliveryLine(order.billing)}
                </p>
              </section>
            ) : null}

            <section className="order-detail__card">
              <h2>{ORDER_UI.summaryHeading}</h2>
              <p className="order-detail__row">
                <span>{ORDER_UI.subtotalLabel}</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </p>
              {order.discountTotal > 0 ? (
                <p className="order-detail__row">
                  <span>{ORDER_UI.discountLabel}</span>
                  <span>−{formatCurrency(order.discountTotal)}</span>
                </p>
              ) : null}
              {order.deliveryFee > 0 ? (
                <p className="order-detail__row">
                  <span>{ORDER_UI.deliveryFeeLabel}</span>
                  <span>{formatCurrency(order.deliveryFee)}</span>
                </p>
              ) : null}
              <p className="order-detail__row order-detail__row--total">
                <span>{ORDER_UI.totalLabel}</span>
                <strong>{formatCurrency(order.total)}</strong>
              </p>
              <p className="order-card__meta">
                {ORDER_UI.paymentLabel}: {paymentStatusLabel(order.paymentStatus)}
              </p>
              {needsPayment ? (
                <button
                  type="button"
                  className="orders-page__pay"
                  disabled={paying}
                  onClick={() => {
                    void pay()
                  }}
                >
                  {paying ? ORDER_UI.paying : ORDER_UI.payNow}
                </button>
              ) : null}
            </section>
          </div>

          {canCancel ? (
            <button
              type="button"
              className="orders-page__cancel"
              disabled={cancelling}
              onClick={() => {
                if (window.confirm(ORDER_UI.cancelConfirm)) {
                  void cancel()
                }
              }}
            >
              {cancelling ? ORDER_UI.cancelling : ORDER_UI.cancel}
            </button>
          ) : null}
        </article>
      )}
    </div>
  )
}
